import { useState, useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Package, Layers, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * StockPickerModal — Reçete satırı için gerçek stok kartı seçici.
 * Aynı endpoint `/api/copilot/materials/search` üzerinden fabrics + accessories araması.
 * Kullanıcı bir kart seçince `onPick({kind, id, code, name, unit, ...})` çağrılır.
 */
export default function StockPickerModal({ open, onClose, onPick, initialQuery = "", onCreateNew }) {
  const [q, setQ] = useState(initialQuery);
  const [kind, setKind] = useState("all"); // all | fabric | accessory
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { if (open) setQ(initialQuery || ""); }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      setBusy(true);
      try {
        const params = new URLSearchParams({ q: q.trim(), limit: "30" });
        if (kind !== "all") params.set("kind", kind);
        const { data } = await api.get(`/copilot/materials/search?${params.toString()}`);
        setResults(data?.results || []);
      } catch (e) {
        toast.error(formatApiError(e) || "Stok araması başarısız");
      } finally { setBusy(false); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, kind, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="stock-picker-modal"
        className="w-[95vw] max-w-[95vw] sm:max-w-2xl lg:max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0 bg-slate-950 border-slate-800"
      >
        <DialogHeader className="px-4 py-3 border-b border-slate-800 bg-slate-900">
          <DialogTitle className="text-slate-100 text-sm flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-400" /> Stoktan Seç
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono ml-2">
              Mevcut kumaş / aksesuar kartları
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 border-b border-slate-800 bg-slate-950/60">
          <div className="flex items-center gap-2">
            <Input
              data-testid="stock-picker-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ad / kod / renk / kategori… (ör: pamuk, YKK, fermuar, düğme)"
              className="h-9 bg-slate-900 border-slate-800 text-sm rounded-sm"
            />
            <div className="flex bg-slate-900 border border-slate-800 rounded-sm">
              {[["all", "Hepsi"], ["fabric", "Kumaş"], ["accessory", "Aksesuar"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setKind(k)}
                  className={`px-2.5 py-1.5 text-[11px] uppercase tracking-wider font-mono transition-colors ${kind === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-100"}`}
                  data-testid={`stock-picker-kind-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {busy && (
            <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aranıyor…
            </div>
          )}
          {!busy && !q.trim() && (
            <div className="text-center py-10 text-slate-500 text-sm">Aramaya başlamak için yukarıya yazın…</div>
          )}
          {!busy && q.trim() && results.length === 0 && (
            <div className="text-center py-10 space-y-3">
              <div className="text-sm text-slate-400">"{q}" için stok kartı bulunamadı.</div>
              {onCreateNew && (
                <Button
                  onClick={() => onCreateNew(q)}
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm text-xs"
                  data-testid="stock-picker-create-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> "{q}" için yeni stok kartı oluştur
                </Button>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            {results.map((r, i) => (
              <button
                key={`${r.kind}-${r.id}`}
                data-testid={`stock-picker-result-${i}`}
                onClick={() => { onPick(r); onClose?.(); }}
                className="w-full text-left grid grid-cols-12 gap-2 items-center p-2 border border-slate-800 hover:border-blue-600 hover:bg-slate-900 rounded-sm transition-colors"
              >
                <div className="col-span-1 flex justify-center">
                  {r.kind === "fabric" ? <Layers className="w-4 h-4 text-cyan-400" /> : <Package className="w-4 h-4 text-fuchsia-400" />}
                </div>
                <div className="col-span-2 text-[11px] font-mono text-slate-400">{r.code || "—"}</div>
                <div className="col-span-6 text-xs text-slate-100">
                  {r.name}
                  {r.color && <span className="text-slate-500 ml-2">· {r.color}</span>}
                </div>
                <div className="col-span-2 text-[10px] text-slate-500 text-right">
                  {r.kind === "fabric"
                    ? `${r.stock_kg || 0} kg / ${r.stock_metre || 0} m`
                    : `${r.stock ?? 0} ${r.unit || "adet"}`}
                </div>
                <div className="col-span-1 flex justify-end">
                  <Badge className={`text-[9px] ${r.kind === "fabric" ? "bg-cyan-900/40 text-cyan-300 border-cyan-800" : "bg-fuchsia-900/40 text-fuchsia-300 border-fuchsia-800"}`}>
                    {r.kind === "fabric" ? "KUMAŞ" : "AKSESUAR"}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
          {!busy && results.length > 0 && onCreateNew && (
            <div className="mt-3 pt-3 border-t border-slate-800 text-center">
              <button
                onClick={() => onCreateNew(q)}
                className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1 mx-auto"
                data-testid="stock-picker-create-inline"
              >
                <Plus className="w-3 h-3" /> Aradığını bulamadın mı? Yeni stok kartı oluştur
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
